// TileWalls displays tile walls including the ghosted effect when user is
// hovering. It is meant to be layered on top of other tiles.

import { TileWallType } from 'app/src/engine/tiles/TileWalls';
import * as React from 'react';

interface Props {
	walls: Set<TileWallType>;
	ghostedWall?: TileWallType | null;
}

export default function TileWalls({ walls, ghostedWall }: Props): React.ReactElement {
	const wallNodes: React.ReactElement[] = [];

	const getSingleWall = (wallType: TileWallType, ghosted = false): JSX.Element => {
		const wallClassNames = ['TileWalls-wall', 'TileWalls-wall--I'];
		let key = '';

		switch (wallType) {
			case TileWallType.Top:
				wallClassNames.push('TileWalls-wall--ITop');
				key = 'TileWalls-wall--ITop';
				break;

			case TileWallType.Right:
				wallClassNames.push('TileWalls-wall--IRight');
				key = 'TileWalls-wall--IRight';
				break;

			case TileWallType.Bottom:
				wallClassNames.push('TileWalls-wall--IBottom');
				key = 'TileWalls-wall--IBottom';
				break;

			case TileWallType.Left:
				wallClassNames.push('TileWalls-wall--ILeft');
				key = 'TileWalls-wall--ILeft';
				break;
		}

		if (ghosted) {
			key += '-ghost';
			wallClassNames.push('TileWalls-wall--ghost');
		}

		return <div key={key} className={wallClassNames.join(' ')} />;
	};

	// If there's a ghost wall specified, make sure we remove it from the list
	// of rendered walls. Then, draw the ghost wall separately.
	if (ghostedWall !== undefined && ghostedWall !== null) {
		if (!walls.has(ghostedWall)) {
			wallNodes.push(getSingleWall(ghostedWall, true));
		}
	}

	// Determine which wall image we should draw.
	if (walls.size === 1) {
		// Add a single wall to the proper side.
		wallNodes.push(getSingleWall(Array.from(walls)[0]));
	} else if (walls.size === 2) {
		// Check if the walls form an L of or if they're across from each other.
		if (
			(walls.has(TileWallType.Top) && walls.has(TileWallType.Bottom)) ||
			(walls.has(TileWallType.Left) && walls.has(TileWallType.Right))
		) {
			// Add single walls for each of the itmes.
			walls.forEach((wallType) => {
				wallNodes.push(getSingleWall(wallType));
			});
		} else {
			// Walls form an L. Create a single L wall and rotate it properly.
			const wallClassNames = ['TileWalls-wall', 'TileWalls-wall--L'];
			let key = '';

			if (walls.has(TileWallType.Left) && walls.has(TileWallType.Bottom)) {
				wallClassNames.push('TileWalls-wall--LBottomLeft');
				key = 'TileWalls-wall--LBottomLeft';
			} else if (walls.has(TileWallType.Left) && walls.has(TileWallType.Top)) {
				wallClassNames.push('TileWalls-wall--LTopLeft');
				key = 'TileWalls-wall--LTopLeft';
			} else if (walls.has(TileWallType.Right) && walls.has(TileWallType.Top)) {
				wallClassNames.push('TileWalls-wall--LTopRight');
				key = 'TileWalls-wall--LTopRight';
			} else {
				wallClassNames.push('TileWalls-wall--LBottomRight');
				key = 'TileWalls-wall--LBottomRight';
			}

			wallNodes.push(<div key={key} className={wallClassNames.join(' ')} />);
		}
	} else if (walls.size === 3) {
		// Walls form a U. Create a single U wall and rotate it properly.
		const wallClassNames = ['TileWalls-wall', 'TileWalls-wall--U'];
		let key = '';

		if (!walls.has(TileWallType.Top)) {
			wallClassNames.push('TileWalls-wall--UOpenTop');
			key = 'TileWalls-wall--UOpenTop';
		} else if (!walls.has(TileWallType.Left)) {
			wallClassNames.push('TileWalls-wall--UOpenLeft');
			key = 'TileWalls-wall--UOpenLeft';
		} else if (!walls.has(TileWallType.Bottom)) {
			wallClassNames.push('TileWalls-wall--UOpenBottom');
			key = 'TileWalls-wall--UOpenBottom';
		} else {
			wallClassNames.push('TileWalls-wall--UOpenRight');
			key = 'TileWalls-wall--UOpenRight';
		}

		wallNodes.push(<div key={key} className={wallClassNames.join(' ')} />);
	} else if (walls.size === 4) {
		// Draw a wall all the way around.
		wallNodes.push(<div key="TileWalls-wall--O" className="TileWalls-wall TileWalls-wall--O" />);
	}

	return <div className="walls">{wallNodes}</div>;
}
